type GoalsPushConfig = {
    ajaxUrl: string;
    nonce: string;
    vapidPublic: string;
    swUrl: string;
    notificationsUrl: string;
    settingsUrl: string;
    optedOut: boolean;
    memberUpdatesOn: boolean;
    reminderSmsOn: boolean;
    missedMeetingSmsOn: boolean;
    unread: number;
    subscribed: boolean;
};

type DeviceRow = {
    id: number;
    label: string;
    added: string;
    endpoint: string;
    device_id: string;
    on: boolean;
};

type SubscribeResult = {
    ok: boolean;
    error?: string;
};

type InboxItem = {
    id: number;
    title: string;
    body: string;
    url: string;
    read: boolean;
    time_ago: string;
    created_label: string;
    type_label: string;
};

function escText(str: string): string {
    return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');
}

let toastTimer = 0;

function showToast(message: string, kind: 'ok' | 'error' = 'ok'): void {
    let el = document.querySelector('.goals-toast') as HTMLElement | null;
    if (!el) {
        el = document.createElement('div');
        el.className = 'goals-toast';
        el.setAttribute('role', 'status');
        document.body.appendChild(el);
    }
    el.textContent = message;
    el.dataset.kind = kind;
    el.hidden = false;
    el.classList.add('is-on');
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => {
        el?.classList.remove('is-on');
        window.setTimeout(() => {
            if (el && !el.classList.contains('is-on')) {
                el.hidden = true;
            }
        }, 220);
    }, 3500);
}

function renderInboxItems(items: InboxItem[]): void {
    const inbox = document.getElementById('notifications-inbox');
    if (!inbox) {
        return;
    }
    if (items.length === 0) {
        inbox.innerHTML = '<p class="notifications-empty">No notifications yet.</p>';
        return;
    }
    inbox.innerHTML =
        '<div class="notifications-list">' +
        items
            .map((item) => {
                const unread = item.read ? '' : ' notifications-item--unread';
                const body = item.body
                    ? '<span class="notifications-item__body">' + escText(item.body) + '</span>'
                    : '';
                const type = item.type_label
                    ? '<span class="notifications-item__type">' + escText(item.type_label) + '</span>'
                    : '';
                return (
                    '<a href="' +
                    escText(item.url) +
                    '" class="notifications-item' +
                    unread +
                    '" data-id="' +
                    item.id +
                    '">' +
                    '<span class="notifications-item__top">' +
                    '<span class="notifications-item__dot" aria-hidden="true"></span>' +
                    type +
                    '<span class="notifications-item__date">' +
                    escText(item.created_label) +
                    '</span></span>' +
                    '<span class="notifications-item__main">' +
                    '<span class="notifications-item__title">' +
                    escText(item.title) +
                    '</span>' +
                    body +
                    '<span class="notifications-item__footer">' +
                    '<span class="notifications-item__time">' +
                    escText(item.time_ago) +
                    '</span></span></span></a>'
                );
            })
            .join('') +
        '</div>';
    inbox.querySelectorAll('.notifications-item').forEach((el) => {
        el.addEventListener('click', () => {
            const id = (el as HTMLElement).dataset.id;
            if (!id) {
                return;
            }
            void post('goals_push_mark_read', { ids: id }).then(() => refreshUnread());
        });
    });
}

function cfg(): GoalsPushConfig | null {
    const c = (window as Window & { goalsPush?: GoalsPushConfig }).goalsPush;
    return c && c.ajaxUrl ? c : null;
}

let localEndpoint: string | null = null;
const DEVICE_ID_KEY = 'goals_push_device_id';

function localDeviceId(): string {
    let id = '';
    try {
        id = localStorage.getItem(DEVICE_ID_KEY) || '';
    } catch {
        id = '';
    }
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(id)) {
        return id.toLowerCase();
    }
    id = crypto.randomUUID();
    try {
        localStorage.setItem(DEVICE_ID_KEY, id);
    } catch {
        return id;
    }
    return id;
}

function localDeviceLabel(): string {
    const ua = navigator.userAgent;
    let browser = 'Browser';
    if (/Edg\//i.test(ua)) {
        browser = 'Edge';
    } else if (/Chrome\//i.test(ua) && !/Edg\//i.test(ua)) {
        browser = 'Chrome';
    } else if (/Firefox\//i.test(ua)) {
        browser = 'Firefox';
    } else if (/Safari\//i.test(ua) && !/Chrome\//i.test(ua)) {
        browser = 'Safari';
    }
    let os = 'Unknown';
    if (/Windows/i.test(ua)) {
        os = 'Windows';
    } else if (/Macintosh|Mac OS X/i.test(ua)) {
        os = 'Mac';
    } else if (/iPhone/i.test(ua)) {
        os = 'iPhone';
    } else if (/iPad/i.test(ua)) {
        os = 'iPad';
    } else if (/Android/i.test(ua)) {
        os = 'Android';
    } else if (/Linux/i.test(ua)) {
        os = 'Linux';
    }
    return browser + ' on ' + os;
}

function isStandalone(): boolean {
    return (
        window.matchMedia('(display-mode: standalone)').matches ||
        !!(window.navigator as Navigator & { standalone?: boolean }).standalone
    );
}

function isIos(): boolean {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

function pushSupported(): boolean {
    return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = window.atob(base64);
    const arr = new Uint8Array(new ArrayBuffer(raw.length));
    for (let i = 0; i < raw.length; ++i) {
        arr[i] = raw.charCodeAt(i);
    }
    return arr;
}

async function post(action: string, data: Record<string, string> = {}): Promise<unknown> {
    const c = cfg();
    if (!c) {
        return null;
    }
    const body = new URLSearchParams({ action, _wpnonce: c.nonce, ...data });
    const res = await fetch(c.ajaxUrl, { method: 'POST', credentials: 'same-origin', body });
    return res.json();
}

function updateBadge(count: number): void {
    document.querySelectorAll('.push-bell__badge').forEach((el) => {
        const badge = el as HTMLElement;
        if (count > 0) {
            badge.textContent = count > 9 ? '9+' : String(count);
            badge.hidden = false;
        } else {
            badge.hidden = true;
        }
    });
}

async function refreshUnread(): Promise<void> {
    const json = (await post('goals_push_unread_count')) as { success?: boolean; data?: { unread?: number } };
    if (json?.success && json.data) {
        updateBadge(json.data.unread ?? 0);
        if (cfg()) {
            (window as Window & { goalsPush?: GoalsPushConfig }).goalsPush!.unread = json.data.unread ?? 0;
        }
    }
}

async function getLocalSubscription(): Promise<PushSubscription | null> {
    if (!pushSupported()) {
        return null;
    }
    const reg = await navigator.serviceWorker.getRegistration('/');
    if (!reg) {
        return null;
    }
    return reg.pushManager.getSubscription();
}

async function refreshLocalSubscription(): Promise<void> {
    const sub = await getLocalSubscription();
    localEndpoint = sub?.endpoint ?? null;
}

async function subscribeDevice(): Promise<SubscribeResult> {
    const c = cfg();
    if (!c || !pushSupported()) {
        return { ok: false, error: 'Not supported in this browser' };
    }
    if (!c.vapidPublic) {
        return { ok: false, error: 'Push is not configured on this site' };
    }
    if (c.optedOut) {
        return { ok: false, error: 'Turn on App notifications first' };
    }
    if (isIos() && !isStandalone()) {
        return { ok: false, error: 'Add to Home Screen first' };
    }
    const perm = await Notification.requestPermission();
    if (perm !== 'granted') {
        return { ok: false, error: 'Permission was not granted' };
    }
    const reg = await navigator.serviceWorker.register(c.swUrl, { scope: '/' });
    await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(c.vapidPublic),
    });
    const json = (await post('goals_push_subscribe', {
        subscription: JSON.stringify(sub.toJSON()),
        device_id: localDeviceId(),
    })) as {
        success?: boolean;
        data?: { message?: string };
    };
    if (!json?.success) {
        return { ok: false, error: json?.data?.message || 'Could not register this device' };
    }
    localEndpoint = sub.endpoint;
    if (cfg()) {
        cfg()!.subscribed = true;
    }
    return { ok: true };
}

async function unsubscribeDevice(): Promise<void> {
    if (!pushSupported()) {
        return;
    }
    const reg = await navigator.serviceWorker.getRegistration('/');
    if (!reg) {
        return;
    }
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
        await post('goals_push_unsubscribe', { subscription: JSON.stringify(sub.toJSON()) });
        await sub.unsubscribe();
    }
    localEndpoint = null;
}

function deviceLocked(): boolean {
    return !pushSupported() || (isIos() && !isStandalone()) || Notification.permission === 'denied';
}

function deviceStatusState(): { state: string; label: string } {
    if (!pushSupported()) {
        return { state: 'unsupported', label: 'Not supported in this browser' };
    }
    if (isIos() && !isStandalone()) {
        return { state: 'ios', label: 'Add to Home Screen first' };
    }
    if (Notification.permission === 'denied') {
        return { state: 'blocked', label: 'Blocked in browser settings' };
    }
    if (localEndpoint) {
        return { state: 'on', label: 'Receiving alerts' };
    }
    if (Notification.permission === 'granted') {
        return { state: 'off', label: 'Permission granted, alerts off' };
    }
    return { state: 'off', label: 'Not receiving alerts' };
}

async function updateDeviceUi(statusOverride?: string): Promise<void> {
    await refreshLocalSubscription();
    const status = document.getElementById('push-device-status');
    const btn = document.getElementById('push-enable-device') as HTMLButtonElement | null;
    const iosHint = document.getElementById('push-ios-hint');
    const info = deviceStatusState();
    if (status) {
        status.dataset.state = statusOverride ? 'error' : info.state;
        const text = status.querySelector('.notifications-device-status__text') || status;
        text.textContent = statusOverride ?? info.label;
    }
    if (iosHint) {
        iosHint.style.display = isIos() && !isStandalone() ? '' : 'none';
    }
    if (btn) {
        btn.hidden = true;
        if (!btn.classList.contains('is-busy')) {
            btn.disabled = true;
        }
    }
}

function isCurrentDevice(device: DeviceRow): boolean {
    const id = localDeviceId();
    if (device.device_id && device.device_id === id) {
        return true;
    }
    return localEndpoint !== null && device.endpoint !== '' && device.endpoint === localEndpoint;
}

function normalizeDevices(devices: DeviceRow[]): DeviceRow[] {
    const rows = devices.map((device) => ({
        id: device.id,
        label: device.label || localDeviceLabel(),
        added: device.added || 'just now',
        endpoint: device.endpoint || '',
        device_id: device.device_id || '',
        on: !!device.on,
    }));
    if (!rows.some((device) => isCurrentDevice(device))) {
        rows.unshift({
            id: 0,
            label: localDeviceLabel(),
            added: 'just now',
            endpoint: localEndpoint || '',
            device_id: localDeviceId(),
            on: !!localEndpoint,
        });
    }
    rows.sort((a, b) => Number(isCurrentDevice(b)) - Number(isCurrentDevice(a)));
    return rows;
}

async function dropLocalSubscription(): Promise<void> {
    if (!pushSupported()) {
        localEndpoint = null;
        return;
    }
    const reg = await navigator.serviceWorker.getRegistration('/');
    const sub = reg ? await reg.pushManager.getSubscription() : null;
    if (sub) {
        await sub.unsubscribe();
    }
    localEndpoint = null;
}

async function touchThisDevice(): Promise<DeviceRow[]> {
    await refreshLocalSubscription();
    const json = (await post('goals_push_touch_device', {
        device_id: localDeviceId(),
        endpoint: localEndpoint || '',
    })) as { success?: boolean; data?: { devices?: DeviceRow[] } };
    return json?.success ? json.data?.devices ?? [] : [];
}

function renderDeviceList(list: HTMLElement, devices: DeviceRow[]): void {
    const rows = normalizeDevices(devices);
    list.innerHTML = rows
        .map((device) => {
            const current = isCurrentDevice(device);
            const on = current ? !!localEndpoint : device.on;
            const badge = current
                ? '<span class="notifications-device-row__current' +
                  (on ? '' : ' is-off') +
                  '">' +
                  (on ? '<span class="notifications-device-row__live"></span>' : '') +
                  'This device</span>'
                : '';
            const action = current || on
                ? '<button type="button" class="notifications-device-row__toggle push-device-toggle' +
                  (on ? ' is-on' : ' is-off') +
                  '"' +
                  (current && !on && (deviceLocked() || !!cfg()?.optedOut) ? ' disabled' : '') +
                  '>' +
                  (on ? 'Turn off' : 'Turn on') +
                  '</button>'
                : '<span class="notifications-device-row__state">Off</span>';
            return (
                '<div class="notifications-device-row' +
                (current ? ' is-current' : '') +
                (on ? '' : ' is-off') +
                '" data-id="' +
                device.id +
                '" data-device="' +
                escText(device.device_id) +
                '">' +
                '<div class="notifications-device-row__info">' +
                '<span class="notifications-device-row__label">' +
                escText(device.label) +
                '</span>' +
                '<span class="notifications-device-row__added">Added ' +
                escText(device.added) +
                '</span>' +
                '</div>' +
                '<div class="notifications-device-row__actions">' +
                badge +
                action +
                '</div>' +
                '</div>'
            );
        })
        .join('');
    list.querySelectorAll('.push-device-toggle').forEach((btn) => {
        btn.addEventListener('click', async () => {
            const toggle = btn as HTMLButtonElement;
            const el = toggle.closest('.notifications-device-row') as HTMLElement | null;
            const id = el?.dataset.id || '';
            const deviceKey = el?.dataset.device || '';
            const row = rows.find((device) => {
                if (id && String(device.id) === id && Number(id) > 0) {
                    return true;
                }
                return deviceKey !== '' && device.device_id === deviceKey;
            });
            if (!row || toggle.disabled || toggle.classList.contains('is-busy')) {
                return;
            }
            const current = isCurrentDevice(row);
            const on = current ? !!localEndpoint : row.on;
            toggle.classList.add('is-busy');
            toggle.disabled = true;
            if (on) {
                const payload: Record<string, string> = {};
                if (row.id > 0) {
                    payload.device_id = String(row.id);
                }
                if (row.device_id) {
                    payload.push_device = row.device_id;
                }
                const removeJson = (await post('goals_push_remove_device', payload)) as {
                    success?: boolean;
                    data?: { devices?: DeviceRow[] };
                };
                if (!removeJson?.success) {
                    toggle.classList.remove('is-busy');
                    toggle.disabled = false;
                    return;
                }
                if (current) {
                    await dropLocalSubscription();
                    if (cfg()) {
                        cfg()!.subscribed = (removeJson.data?.devices ?? []).some((device) => device.on);
                    }
                }
                await updateDeviceUi();
                renderDeviceList(list, removeJson.data?.devices ?? []);
                return;
            }
            if (!current) {
                toggle.classList.remove('is-busy');
                toggle.disabled = false;
                return;
            }
            const result = await subscribeDevice();
            await updateDeviceUi(result.ok ? undefined : result.error);
            await refreshDeviceList();
        });
    });
}

async function refreshDeviceList(): Promise<void> {
    const list = document.getElementById('push-device-list');
    if (!list) {
        return;
    }
    await refreshLocalSubscription();
    const json = (await post('goals_push_devices')) as {
        success?: boolean;
        data?: { devices?: DeviceRow[] };
    };
    const devices = json?.success ? json.data?.devices ?? [] : [];
    const current = devices.find((device) => isCurrentDevice(device));
    if (current && !current.on && localEndpoint) {
        await dropLocalSubscription();
        if (cfg()) {
            cfg()!.subscribed = devices.some((device) => device.on);
        }
    }
    renderDeviceList(list, devices);
}

function initBell(): void {
    const c = cfg();
    if (!c) {
        return;
    }
    updateBadge(c.unread ?? 0);
}

async function initSoftPrompt(): Promise<void> {
    const c = cfg();
    if (!c || c.optedOut || !pushSupported()) {
        return;
    }
    const sub = await getLocalSubscription();
    if (sub) {
        return;
    }
    if (Notification.permission !== 'default') {
        return;
    }
    if (isIos() && !isStandalone()) {
        return;
    }
    if (localStorage.getItem('goals_push_prompt_dismissed') === '1') {
        return;
    }
    if (document.querySelector('.push-soft-prompt')) {
        return;
    }
    const el = document.createElement('div');
    el.className = 'push-soft-prompt';
    el.innerHTML =
        '<div class="push-soft-prompt__body">' +
        '<span class="push-soft-prompt__icon" aria-hidden="true">' +
        '<svg class="push-soft-prompt__bell" viewBox="0 0 24 24">' +
        '<path d="M12 22a2.5 2.5 0 0 0 2.45-2h-4.9A2.5 2.5 0 0 0 12 22Zm7-6V11a7 7 0 0 0-5-6.71V4a2 2 0 1 0-4 0v.29A7 7 0 0 0 5 11v5l-2 2v1h18v-1l-2-2Z"/>' +
        '</svg>' +
        '</span>' +
        '<div class="push-soft-prompt__copy">' +
        '<p class="push-soft-prompt__text">Get notified when members post</p>' +
        '<div class="push-soft-prompt__actions">' +
        '<button type="button" class="btn btn-primary btn-sm push-soft-prompt__enable">Enable</button>' +
        '<button type="button" class="btn btn-primary-outline btn-sm push-soft-prompt__dismiss">Not now</button>' +
        '</div>' +
        '</div>' +
        '</div>';
    document.body.appendChild(el);
    el.querySelector('.push-soft-prompt__dismiss')?.addEventListener('click', () => {
        localStorage.setItem('goals_push_prompt_dismissed', '1');
        el.remove();
    });
    el.querySelector('.push-soft-prompt__enable')?.addEventListener('click', async () => {
        await subscribeDevice();
        el.remove();
        void updateDeviceUi();
        void refreshDeviceList();
    });
}

function isNotificationsSettingsHash(hash: string): boolean {
    const h = hash.replace(/^#/, '');
    return h === 'settings' || h === 'notifications-settings';
}

function setNotificationsView(settings: boolean, updateHash: boolean): void {
    const inbox = document.getElementById('notifications-inbox');
    const settingsEl = document.getElementById('notifications-settings');
    const toggle = document.querySelector('.notifications-toggle') as HTMLButtonElement | null;
    if (!inbox || !settingsEl || !toggle) {
        return;
    }
    inbox.style.display = settings ? 'none' : '';
    settingsEl.style.display = settings ? '' : 'none';
    toggle.classList.toggle('is-settings', settings);
    toggle.setAttribute('aria-label', settings ? 'Notifications' : 'Settings');
    if (updateHash) {
        const next = settings ? '#notifications-settings' : '#notifications-inbox';
        if (window.location.hash !== next) {
            history.pushState(null, '', next);
        }
    }
}

function initNotificationsPage(): void {
    const root = document.querySelector('.notifications-page');
    if (!root) {
        return;
    }
    void (async () => {
        const devices = await touchThisDevice();
        const current = devices.find((device) => isCurrentDevice(device));
        if (current && !current.on && localEndpoint) {
            await dropLocalSubscription();
        }
        await updateDeviceUi();
        const list = document.getElementById('push-device-list');
        if (list) {
            renderDeviceList(list, devices);
        } else {
            await refreshDeviceList();
        }
    })();
    const toggle = root.querySelector('.notifications-toggle') as HTMLButtonElement | null;
    toggle?.addEventListener('click', () => {
        setNotificationsView(!toggle.classList.contains('is-settings'), true);
    });
    root.querySelector('#notifications-view-inbox')?.addEventListener('click', () => {
        setNotificationsView(false, true);
    });
    window.addEventListener('hashchange', () => {
        setNotificationsView(isNotificationsSettingsHash(window.location.hash), false);
    });
    window.addEventListener('popstate', () => {
        setNotificationsView(isNotificationsSettingsHash(window.location.hash), false);
    });
    setNotificationsView(isNotificationsSettingsHash(window.location.hash), false);
    root.querySelector('#push-enable-device')?.addEventListener('click', async () => {
        const btn = root.querySelector('#push-enable-device') as HTMLButtonElement | null;
        if (!btn || btn.disabled || btn.classList.contains('is-busy') || localEndpoint) {
            return;
        }
        btn.classList.add('is-busy');
        btn.disabled = true;
        const result = await subscribeDevice();
        await updateDeviceUi(result.ok ? undefined : result.error);
        btn.classList.remove('is-busy');
        void refreshDeviceList();
    });
    root.querySelector('#push-save-settings')?.addEventListener('click', async () => {
        const pushEnabled = (root.querySelector('#push_enabled') as HTMLInputElement | null)?.checked ? '1' : '';
        const memberUpdates = (root.querySelector('#member_updates') as HTMLInputElement | null)?.checked ? '1' : '';
        const reminderSms = (root.querySelector('#reminder_sms') as HTMLInputElement | null)?.checked ? '1' : '';
        const missedMeetingSms = (root.querySelector('#missed_meeting_sms') as HTMLInputElement | null)?.checked ? '1' : '';
        if (!pushEnabled) {
            await unsubscribeDevice();
            if (cfg()) {
                cfg()!.subscribed = false;
                cfg()!.optedOut = true;
            }
        } else if (cfg()) {
            cfg()!.optedOut = false;
        }
        const json = (await post('goals_push_save_settings', {
            push_enabled: pushEnabled,
            member_updates: memberUpdates,
            reminder_sms: reminderSms,
            missed_meeting_sms: missedMeetingSms,
        })) as { success?: boolean; data?: GoalsPushConfig };
        if (json?.success && json.data && cfg()) {
            Object.assign(cfg()!, json.data);
        }
        await updateDeviceUi();
        void refreshDeviceList();
        showToast('Settings saved');
    });
    root.querySelector('#push-send-test')?.addEventListener('click', async () => {
        const btn = root.querySelector('#push-send-test') as HTMLButtonElement | null;
        if (btn) {
            btn.disabled = true;
            btn.classList.add('working');
            window.setTimeout(() => {
                btn.classList.remove('working');
            }, 1000);
        }
        const json = (await post('goals_push_send_test')) as {
            success?: boolean;
            data?: { sent?: number; unread?: number; items?: InboxItem[]; message?: string };
        };
        if (btn) {
            btn.disabled = false;
        }
        const ok = !!json?.success;
        showToast(
            ok ? json.data?.message || 'Test sent' : json?.data?.message || 'Could not send test',
            ok ? 'ok' : 'error'
        );
        if (ok && json.data?.items) {
            renderInboxItems(json.data.items);
        }
        if (ok && json.data?.unread !== undefined) {
            updateBadge(json.data.unread);
        }
    });
    root.querySelectorAll('.notifications-item').forEach((item) => {
        item.addEventListener('click', () => {
            const id = (item as HTMLElement).dataset.id;
            if (!id) {
                return;
            }
            void post('goals_push_mark_read', { ids: id }).then(() => refreshUnread());
        });
    });
}

export function pushNotifications(): void {
    initBell();
    void initSoftPrompt();
    initNotificationsPage();
    const onVisible = (): void => {
        if (document.visibilityState === 'hidden') {
            return;
        }
        void refreshUnread();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
}
